import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { Grid, Layout, Menu, Button, Avatar, Popover, Typography, Flex, Alert, message, Dropdown } from 'antd';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  LogoutOutlined,
  SettingOutlined,
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  UsergroupAddOutlined,
  CreditCardOutlined,
  DatabaseOutlined,
  BankOutlined,
  MoonOutlined,
  SunOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthData, getBackToAdmin, isUserOriginAdmin } from '../../../Config/ApiConfig';
import { fbLogout } from '../../../Utils/facebook';
import { useGlobal } from '../GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import { getUser } from './LayoutServices';
import { sendVerificationEmail } from '../../Profile/ProfileServices';
import { useTheme } from '../GlobalContext/ThemeContext';
import { ReactComponent as LogoWhite } from '../../../Images/logo-wide-white.svg';
import { ReactComponent as Logo } from '../../../Images/logo-wide.svg';
import { useTranslation } from 'react-i18next';
import { RTL_LANGUAGES } from '../../../Constants/language';
import useSocket from '../../../Hooks/useSocket';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../Constants/socket';
const { useBreakpoint } = Grid;
const { Header, Sider, Content } = Layout;
const { Text } = Typography;


const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dropdownRef = useRef(null);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { handleError } = useErrorHandler();
  const { user: { first_name, last_name, picture_url, is_admin, has_received_welcome_credit = true, balance, id, email } = {}, changeUser } = useGlobal();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [balanceUpdated, setBalanceUpdated] = useState(false);
  const screens = useBreakpoint();
  const isOriginAdmin = isUserOriginAdmin();
  const contentBorderColor = isDarkMode ? '#434343' : '#f0f0f0';
  
  // Socket connections
  const { socket, connectionState } = useSocket();
  const isConnected = connectionState?.status === 'connected';
  
  // Subscribe to user resource to receive real-time balance updates
  useSocketSubscription(Resource.USER, id ? [id] : null);
  
  // Listen for coin balance updates
  useSocketEvent(Resource.COIN_BALANCE, Action.UPDATE, (data) => {
    if (data && data.user_id === id) {
      // Update the user's balance in the global context
      changeUser(prevUser => ({
        ...prevUser,
        balance: {
          ...prevUser.balance,
          available_coins: data.available_coins,
          total_coins_bought: data.total_coins_bought,
          total_coins_consumed: data.total_coins_consumed
        }
      }));
      
      // Trigger animation effect
      setBalanceUpdated(true);
      setTimeout(() => setBalanceUpdated(false), 2000);
    }
  });

  const isRtl = RTL_LANGUAGES.includes(i18n.language.split('-')[0]);

  const getUserCall = useCallback(async () => {
    try {
      const { data } = await getUser();
      changeUser(data);
    } catch (error) {
      handleError(error);
    }
  }, [changeUser, handleError]);

  useEffect(() => {
    getUserCall();
  }, [getUserCall]);

  useEffect(() => {
    if (screens.md) {
      setMobileSidebarVisible(false);
    }
  }, [screens.md]);

  const onhandleLogout = async () => {
    // Disconnect from socket before logout
    if (socket && socket.disconnect) {
      socket.disconnect();
    }
    
    await fbLogout();
    navigate('/login');
    clearAuthData();
  };

  const navigateToProfile = () => {
    navigate('/profile');
  };

  const toggleCollapse = () => {
    if (screens.md) {
      setCollapsed(!collapsed);
    } else {
      setMobileSidebarVisible(!mobileSidebarVisible);
    }
  };

  const handleVerifyEmail = async () => {
    if (!email) {
      message.error(t('please_add_email_to_verify'));
      navigate('/profile');
      return;
    }
    setVerificationLoading(true);
    try {
      await sendVerificationEmail(id);
      message.success(t('verification_email_sent'));
    } catch (error) {
      handleError(error);
    } finally {
      setVerificationLoading(false);
    }
  };

  const backAdminProfile = () => {
    const user = getBackToAdmin();
    changeUser(user);
  };

  const menuItems = [
    ...(is_admin ? [{ key: '0', icon: <UsergroupAddOutlined />, label: t('users'), url: '/users' }] : []),
    { key: '1', icon: <DashboardOutlined />, label: t('dashboard'), url: '/' },
    { key: '3', icon: <TeamOutlined />, label: t('agents'), url: '/agents' },
    { key: '5', icon: <MessageOutlined />, label: t('messages'), url: '/messages' },
    { key: '4', icon: <DatabaseOutlined />, label: t('knowledge_base'), url: '/knowledge-base' },
    { key: '2', icon: <CreditCardOutlined />, label: t('billing'), url: '/billing' },
    { key: '6', icon: <BankOutlined />, label: t('transactions'), url: '/transactions' },
    { key: '7', icon: <SettingOutlined />, label: t('settings'), url: '/settings' },
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: '中文' },
    { code: 'ar', name: 'العربية' }
  ];

  const languageItems = languages.map((lang) => ({
    key: lang.code,
    label: (
      <div onClick={() => {
        console.log('Language selected:', lang.code);
        i18n.changeLanguage(lang.code);
      }}>
        {lang.name}
      </div>
    ),
  }));
  const LanguageDropdown = forwardRef(({ dropdownRef }) => (
    <Dropdown ref={dropdownRef} menu={{ items: languageItems }} trigger={['click']} placement="bottomLeft" arrow>
      <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', width: '100%' }}>
        <GlobalOutlined style={{ color: isDarkMode ? '#fff' : '#001529', marginInlineEnd: 8 }} />
        {!collapsed && <span style={{ marginInlineStart: 0 }}>{t('switch_language')}</span>}
      </div>
    </Dropdown>
  ));

  const bottomMenuItems = [
    {
      key: 'language',
      icon: <LanguageDropdown dropdownRef={dropdownRef} />,
      label: collapsed ? <LanguageDropdown dropdownRef={dropdownRef} /> : t('language'),
      style: { marginTop: 'auto' },
    },
    {
      key: 'theme',
      icon: isDarkMode ? <SunOutlined style={{ color: '#ffd666' }} /> : <MoonOutlined style={{ color: '#597ef7' }} />,
      label: collapsed ? (isDarkMode ? t('light_mode') : t('dark_mode')) : (isDarkMode ? t('light_mode') : t('dark_mode')),
      onClick: toggleDarkMode,
    },
  ];


  const userMenuContent = (
    <div style={{ minWidth: 200 }}>
      {screens.xs && (
        <Flex gap="small" align="center" style={{ padding: 12, borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
          <Avatar size={40} style={{ backgroundColor: '#001529' }} src={picture_url} icon={!picture_url && <UserOutlined />} />
          <Flex vertical>
            <Text strong>{first_name} {last_name}</Text>
            <Text 
              type="secondary"
              style={{ 
                transition: 'background-color 0.3s ease',
                backgroundColor: balanceUpdated ? (isDarkMode ? 'rgba(0, 230, 118, 0.1)' : 'rgba(0, 230, 118, 0.1)') : 'transparent',
                padding: balanceUpdated ? '0 4px' : '0',
                borderRadius: '2px'
              }}
            >
              {balance?.available_coins} Coins
            </Text>
          </Flex>
        </Flex>
      )}
      <Menu>
        {isOriginAdmin && (
          <Menu.Item key="1" icon={<UserOutlined />} onClick={backAdminProfile}>
            {t('get_back_to_admin')}
          </Menu.Item>
        )}
        <Menu.Item key="2" icon={<UserOutlined />} onClick={navigateToProfile}>
          {t('profile')}
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key="3" icon={<LogoutOutlined />} onClick={onhandleLogout}>
          {t('log_out')}
        </Menu.Item>
      </Menu>
    </div>
  );

  const activeKey = menuItems.find(item => location.pathname === item.url)?.key;

  const sidebarContent = (
    <>
      <div dir='ltr' style={{
        height: 32,
        margin: '16px 23px',
        color: isDarkMode ? '#fff' : '#001529',
        fontWeight: 'bold',
        fontSize: 20,
        overflow: 'hidden',
        display: 'block',
        textAlign: 'center',
      }} align="center" justify="center">
        {isDarkMode && <LogoWhite style={{ width: 'auto', height: '32px' }} />}
        {
          !isDarkMode && <Logo style={{ width: 'auto', height: '32px', }} />
        }
      </div>
      <Menu
        mode="inline"
        style={{
          background: isDarkMode ? 'rgb(31, 31, 31)' : '#fff',
          borderInlineEnd: 'none',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - 64px)'
        }}
        selectedKeys={[activeKey]}
        items={[
          ...menuItems.map(item => ({
            key: item.key,
            icon: React.cloneElement(item.icon, {
              style: { color: isDarkMode ? '#fff' : '#001529', transition: 'all 0.3s ease' }
            }),
            label: item.url ?
              <Link to={item.url} style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)', transition: 'all 0.3s ease' }}>{item.label}</Link> :
              item.label,
            onClick: item.onClick,
            style: item.style,
            title: typeof item.label === 'string' ? item.label : undefined
          })),
          ...bottomMenuItems.map(item => ({
            key: item.key,
            icon: React.cloneElement(item.icon, {
              style: { color: isDarkMode ? '#fff' : '#001529', transition: 'all 0.3s ease' }
            }),
            label: item.label,
            onClick: item.onClick,
            style: item.style,
            title: typeof item.label === 'string' ? item.label : undefined
          }))
        ]}
      />
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: isDarkMode ? '#141414' : '#f0f2f5' }}>
      {screens.md ? (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: isRtl ? 'auto' : 0,
            right: isRtl ? 0 : 'auto',
            top: 0,
            bottom: 0,
            zIndex: 2,
            backgroundColor: isDarkMode ? 'rgb(31, 31, 31)' : '#fff',
            borderRight: isRtl ? 'none' : (isDarkMode ? `1px solid ${contentBorderColor}` : '1px solid #f0f0f0'),
            borderLeft: isRtl ? (isDarkMode ? `1px solid ${contentBorderColor}` : '1px solid #f0f0f0') : 'none',
          }}
        >
          {sidebarContent}
        </Sider>
      ) : (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, display: mobileSidebarVisible ? 'block' : 'none' }} onClick={() => setMobileSidebarVisible(false)}>
          <div style={{
            position: 'fixed',
            top: 0,
            insetInlineStart: isRtl ? 'auto' : 0,
            insetInlineEnd: isRtl ? 0 : 'auto',
            width: 200,
            height: '100%',
            backgroundColor: isDarkMode ? '#001529' : '#fff',
            overflowY: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            {sidebarContent}
          </div>
        </div>
      )}
      <Layout className="site-layout" style={{
        marginLeft: isRtl ? 0 : (screens.md ? (collapsed ? 80 : 200) : 0),
        marginRight: isRtl ? (screens.md ? (collapsed ? 80 : 200) : 0) : 0,
        transition: isRtl ? 'margin-right 0.2s' : 'margin-left 0.2s'
      }}>
        <Header
          className="site-layout-background"
          style={{
            padding: 0,
            background: isDarkMode ? 'rgb(31, 31, 31)' : '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            width: '100%',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 4px 0px',
            borderBottom: isDarkMode
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >          <Flex justify="space-between" align="center" style={{ height: '100%', paddingInlineEnd: 24 }} gap={16}>
            <Button
              type="text"
              icon={screens.md ? (collapsed ? (isRtl ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />) : (isRtl ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)) : <MenuUnfoldOutlined />}
              onClick={toggleCollapse}
              style={{
                fontSize: '16px', width: 64, height: 64, color: isDarkMode ? '#fff' : '#001529'
              }}
            />
            <Flex align="center" style={{ flexShrink: 0 }}>
              <Popover content={userMenuContent} trigger="click" placement="bottomRight">
                <Flex align="center" style={{ cursor: 'pointer', padding: '8px 0' }}>
                  <Avatar style={{
                    backgroundColor: isDarkMode ? '#001529' : '#f0f0f0',
                    height: 40, width: 40,
                    color: isDarkMode ? '#fff' : '#001529'
                  }} src={picture_url} icon={!picture_url && <UserOutlined />} />
                  {screens.sm && (
                    <Flex vertical style={{ marginInlineStart: 8 }}>
                      <Text style={{ color: isDarkMode ? '#fff' : '#001529' }}>{first_name} {last_name}</Text>
                      <Text 
                        style={{ 
                          color: isDarkMode ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.88)', 
                          fontWeight: 'bold',
                          transition: 'color 0.3s ease',
                          backgroundColor: balanceUpdated ? (isDarkMode ? 'rgba(0, 230, 118, 0.1)' : 'rgba(0, 230, 118, 0.1)') : 'transparent',
                        }}
                      >
                        {balance?.available_coins} {t('coins')}
                      </Text>
                    </Flex>
                  )}
                </Flex>
              </Popover>
            </Flex>
          </Flex>
        </Header>
        <Content
          theme={isDarkMode ? 'dark' : 'light'}
          style={{
            padding: 16,
            height: location.pathname === '/messages' ? 'calc(100vh - 64px)' : 'auto',
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!has_received_welcome_credit && (
            <Alert
              message={t('verify_email_and_get_free_coins')}
              type="info"
              showIcon
              style={{ width: '100%', marginBottom: 8 }}
              action={<Button size="small" type="primary" loading={verificationLoading} onClick={handleVerifyEmail}>{t('verify_now')}</Button>}
              closable
            />
          )}
          {children}
        </Content>
      </Layout>
    </Layout >
  );
};

export default AdminLayout;
